/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* tslint:disable: no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import MsDyn365, { IImageData } from '@msdyn365-commerce/core';
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { getPromoAnalyticsConfig } from '../../themes/mattressfirm/views/tealiumAnalytics/analytics';
import { fireAnalyticsLink, IAnalyticsConfiguration } from '../../Utilities/analytics/clickTrack';
import { htmltoText } from '../../Utilities/analytics/htmlToText';
import { firePromotionClick, firePromotionImpression } from '../../Utilities/analytics/promotionClickTrack';
import { getDynamicImageSettings, getResponsiveImage, getResponsiveFromSeoData } from '../../Utilities/get-dynamic-image';
import _getViewPort from '../../Utilities/get-view-port';
import LinksComponent from './components/links';
import { IContentBlockViewProps } from './single-category-banner';
import CustomSkeleton from '../../themes/mattressfirm/custom-components/CustomSkeleton';
// tslint:disable-next-line: max-func-body-length cyclomatic-complexity
const ContentBlockView: React.FC<IContentBlockViewProps> = (props) => {
  const {
    contentBlockContainer,
    imageContainer,
    detailsContainer,
    title,
    text,
    links,
    image,
    contentBlockAnchorTag,
    imageLink,
    useSeoData,
    seoData,
    useDynamicImages,
    dynamicImages,
    imageAriaLabel,
    unbxdBannerHTML,
    unbxdImageURL
  } = props;
  const { tealiumEvent, enhancedEcommTracking, tealiumPromotion, heading, paragraph } = props.config;

  const isPromotion = enhancedEcommTracking === 'promotion';
  const pagePath = MsDyn365.isBrowser ? (window.location.href?.split(location.host)[1]?.includes('?') ? window.location.href.split(location.host)[1].split('?')[0] : window.location.href.split(location.host)[1]) : '';
  const paragraphText = paragraph ? htmltoText(paragraph.toString()).trim() : '';
  let promoAnalyticsConfig = getPromoAnalyticsConfig(props.config.image, imageLink || links && links[0]?.linkUrl?.destinationUrl, pagePath, paragraphText, tealiumPromotion, true, heading?.text);

  const [ref, inView] = useInView({
    triggerOnce: true,
    rootMargin: '25px'
  });

  const handleAnalytics = (isImageClick?: boolean, ctaUrl?: string) => {
    if (MsDyn365.isBrowser) {
      const eventActionDefault = isImageClick ? 'image click' : 'CTA click';
      const categoryBannerAnalyticsConfig: IAnalyticsConfiguration = {
        eventName: tealiumEvent?.event || 'category-banner',
        eventCategory: tealiumEvent?.event_category || 'category page banner',
        eventAction: tealiumEvent?.event_action || eventActionDefault,
        eventLabel: tealiumEvent?.event_label || heading?.text
      };
      fireAnalyticsLink({ ...categoryBannerAnalyticsConfig });
      if (isPromotion) {
        if (!isImageClick) {
          promoAnalyticsConfig = getPromoAnalyticsConfig(props.config.image, ctaUrl, pagePath, paragraphText, tealiumPromotion, true, heading?.text);
        }
        promoAnalyticsConfig && firePromotionClick({ ...promoAnalyticsConfig });
      }

      if (imageLink) {
        window.location.href = imageLink;
      } else if (!isImageClick && ctaUrl) {
        window.location.href = ctaUrl;
      }
    }
  };

  React.useEffect(() => {
    if (MsDyn365.isBrowser && isPromotion && inView) {
      promoAnalyticsConfig && firePromotionImpression({ ...promoAnalyticsConfig });
    }
  }, [inView]);

  let currentImage = image;

  if (useSeoData) {
    if (seoData && seoData['Banner State'].includes('Active')) {
      const desktopImage: IImageData = {
        src: seoData['Desktop Banner URL'],
        altText: seoData['H1 Tag'],
        title: seoData['H1 Tag']
      };
      const tabletImage: IImageData = {
        src: seoData['Tablet Banner URL'],
        altText: seoData['H1 Tag'],
        title: seoData['H1 Tag']
      };
      const mobileImage: IImageData = {
        src: seoData['Mobile Banner URL'],
        altText: seoData['H1 Tag'],
        title: seoData['H1 Tag']
      };

      currentImage = getResponsiveFromSeoData({
        desktop: desktopImage,
        tablet: tabletImage,
        mobile: mobileImage,
        imageSettingViewports: getDynamicImageSettings({ moduleName: 'mfrm-category-banner' })
      });
    }

    return (
      <>
        <div className='category-banner__dynamic-image'>
          {currentImage}

        </div>
        {seoData && seoData['Banner State'].includes('Active') && <CustomSkeleton className='single-category-banner-skeleton' type='text' />}
      </>

    );
  } else if (unbxdImageURL || unbxdBannerHTML) {
    const unbxdImage = unbxdImageURL && (
      <>
        <Msdyn365.Image className='single-category-banner-image' src={unbxdImageURL} gridSettings={props.context.request.gridSettings || {}} editProps={{ key: props.config.image || {}, requestContext: props.context.request }} />
        <CustomSkeleton className='single-category-banner-skeleton' type='text' />
      </>

    );
    return (
      <Module {...contentBlockContainer}>
        <Node
          {...contentBlockAnchorTag}
          ref={ref}
          href={imageLink}
          className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
        >
          <Node {...imageContainer}>
            {unbxdBannerHTML ? unbxdBannerHTML : unbxdImage}
          </Node>
        </Node>
      </Module>
    );
    // Only use Responsive Image if Dynamic Images are defined
  } else if (dynamicImages && useDynamicImages) {
    currentImage = getResponsiveImage({
      desktop: dynamicImages?.desktopImage,
      tablet: dynamicImages?.tabletImage,
      mobile: dynamicImages?.mobileImage,
      defaultImg: props.config.image,
      imageSettingViewports: getDynamicImageSettings({ moduleName: 'mfrm-category-banner' })
    });
  }
  if (useDynamicImages) {
    return (
      <>
        <div className='category-banner__dynamic-image'> {currentImage} </div>
        {currentImage && <CustomSkeleton className='single-category-banner-skeleton' type='text' />}
      </>

    );
  }

  if (imageLink) {
    return (
      <Module {...contentBlockContainer}>
        <Node
          {...contentBlockAnchorTag}
          href={imageLink}
          ref={ref}
          className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
          aria-label={imageAriaLabel}
        >
          <Node
            {...imageContainer}
            // tslint:disable-next-line: jsx-no-lambda
            onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
              event.preventDefault();
              handleAnalytics(true, undefined);
            }}
          >
            {image}
          </Node>
        </Node>
        <Node {...detailsContainer}>
          {title}
          {text}
          {links}
        </Node>
      </Module>
    );
  } else {
    return (
      <Module {...contentBlockContainer}>
        <Node {...imageContainer} ref={ref}>{image}</Node>
        <Node {...detailsContainer}>
          {title}
          {text}
          {props.links && props.links.length > 0 && props.handleTextChange && props.telemetryContent &&
            <LinksComponent
              {...{
                links: props.links,
                onTextChange: props.handleTextChange,
                requestContext: props.context.request,
                telemetryContent: props.telemetryContent,
                onClickOverride: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, ctaUrl: string) => {
                  event.preventDefault();
                  handleAnalytics(false, ctaUrl);
                }
              }}
            />}
        </Node>
      </Module>
    );
  }
};

export default ContentBlockView;
